import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import './Range.less';
import { ComponentChild } from 'preact';

export type RangeProps = {
    className?: string;
    min: number;
    max: number;
    values: [number, number];
    onChange: (values: [number, number]) => void;
    labelFormatter?: (value: number) => ComponentChild;
};

const Range = ({
    className = '',
    min,
    max,
    values,
    onChange,
    labelFormatter = (value: number) => value.toFixed(0)
}: RangeProps) => {
    const [state, setState] = useState({
        isSelDown: false,
        indexSelDown: 0,
        moveStartValue: 0,
        moveCurrentValue: 0,
        moveStartX: 0,
        moveCurrentX: 0,
        boxWidth: 0
    });
    const ref = useRef<HTMLDivElement>(null);
    const getClientX = (event: MouseEvent | TouchEvent): number => {
        return (event as TouchEvent).touches &&
            (event as TouchEvent).touches.length > 0
            ? (event as TouchEvent).touches[0].clientX
            : (event as MouseEvent).clientX;
    };
    const getCurrentValues = (): [number, number] => {
        return state.isSelDown
            ? state.indexSelDown === 0
                ? [state.moveCurrentValue, values[1]]
                : [values[0], state.moveCurrentValue]
            : values;
    };
    const getSidePercentage = (side: 'left' | 'right'): number => {
        const diff = max - min;
        const currentValues = getCurrentValues();
        if (side === 'left') {
            return ((currentValues[0] - min) * 100) / diff;
        } else {
            return ((max - currentValues[1]) * 100) / diff;
        }
    };
    const startToMove = useCallback(
        (event: MouseEvent | TouchEvent, index: number) => {
            if (!ref.current || ref.current.offsetWidth < 1) {
                return;
            }
            const clientX = getClientX(event);
            setState({
                isSelDown: true,
                indexSelDown: index,
                moveStartValue: values[index],
                moveCurrentValue: values[index],
                moveStartX: clientX,
                moveCurrentX: clientX,
                boxWidth: ref.current.offsetWidth
            });
            event.stopPropagation();
        },
        [ref.current, values]
    );

    const getMoveCurrentValue = useCallback(
        (moveCurrentX: number) => {
            const moveBoxProportion =
                (moveCurrentX - state.moveStartX) / state.boxWidth;
            // if (state.reverse) {
            //     moveBoxProportion = moveBoxProportion * -1;
            // }
            const size = max - min;
            const moveIntoLimit = size * moveBoxProportion;
            let moveCurrentValue = state.moveStartValue + moveIntoLimit;
            moveCurrentValue = Math.max(min, moveCurrentValue);
            moveCurrentValue = Math.min(max, moveCurrentValue);
            if (state.indexSelDown === 0) {
                moveCurrentValue = Math.min(values[1], moveCurrentValue);
            } else {
                moveCurrentValue = Math.max(values[0], moveCurrentValue);
            }

            return Math.round(moveCurrentValue);
        },
        [state, max, min]
    );

    const move = useCallback(
        (event: MouseEvent | TouchEvent) => {
            if (!state.isSelDown) {
                return;
            }
            const clientX = getClientX(event);
            setState({
                ...state,
                moveCurrentX: clientX,
                moveCurrentValue: getMoveCurrentValue(clientX)
            });
            event.stopPropagation();
            event.preventDefault();
        },
        [ref.current, state, getMoveCurrentValue]
    );

    const stopToMove = useCallback(
        (event: MouseEvent | TouchEvent) => {
            event.stopPropagation();
            if (state.isSelDown) {
                onChange(getCurrentValues());
                setState({
                    ...state,
                    isSelDown: false
                });
            }
        },
        [onChange, state]
    );

    useEffect(() => {
        document.addEventListener('mousemove', move);
        document.addEventListener('mouseleave', stopToMove);
        document.addEventListener('mouseup', stopToMove);
        document.addEventListener('touchmove', move);
        document.addEventListener('touchend', stopToMove);
        return () => {
            document.removeEventListener('mousemove', move);
            document.removeEventListener('mouseleave', stopToMove);
            document.removeEventListener('mouseup', stopToMove);
            document.removeEventListener('touchmove', move);
            document.removeEventListener('touchend', stopToMove);
        };
    }, [move, stopToMove]);

    return (
        <div className={`bri-Range ${className}`} ref={ref}>
            <div className="bri-Range-labels bri-Body2BoldFont">
                <span>
                    {labelFormatter(
                        state.isSelDown && state.indexSelDown === 0
                            ? getCurrentValues()[0]
                            : values[0]
                    )}
                </span>
                <span>
                    {labelFormatter(
                        state.isSelDown && state.indexSelDown === 1
                            ? getCurrentValues()[1]
                            : values[1]
                    )}
                </span>
            </div>
            <div className="bri-Range-line">
                <div
                    className="bri-Range-line-selected"
                    style={{
                        left: `${getSidePercentage('left')}%`,
                        right: `${getSidePercentage('right')}%`
                    }}
                />
                <div
                    className={`bri-Range-button bri-left ${
                        state.isSelDown && state.indexSelDown === 0
                            ? 'bri-active'
                            : ''
                    }`}
                    style={{ left: `${getSidePercentage('left')}%` }}
                    onMouseDown={event => startToMove(event, 0)}
                    onTouchStart={event => startToMove(event, 0)}
                />
                <div
                    className={`bri-Range-button bri-right ${
                        state.isSelDown && state.indexSelDown === 1
                            ? 'bri-active'
                            : ''
                    }`}
                    style={{ right: `${getSidePercentage('right')}%` }}
                    onMouseDown={event => startToMove(event, 1)}
                    onTouchStart={event => startToMove(event, 1)}
                />
            </div>
        </div>
    );
};

export default Range;
